import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api } from "../../lib/api";
import { SETTINGS_TABS, SETTINGS_TAB_PARAM, settingsAttention, settingsTabOf } from "../../lib/settingsTabs";
import EnrolmentSettings from "./settings/EnrolmentSettings";
import PaymentSettings from "./settings/PaymentSettings";
import ContractSettings from "./settings/ContractSettings";
import LogiscoolSettings from "./settings/LogiscoolSettings";
import SiteSettings from "./settings/SiteSettings";
import ReferralSettings from "./settings/ReferralSettings";
import EmailSettings from "./settings/EmailSettings";
import AssessmentSettings from "./settings/AssessmentSettings";
import FeedbackSettings from "./settings/FeedbackSettings";

// One panel per tab (`lib/settingsTabs.js`). The cards themselves live in
// `./settings/`, one file per tab; this page loads the settings, saves them
// and shows the tab the address asks for.
const PANELS = {
  inscriere: EnrolmentSettings,
  plati: PaymentSettings,
  contracte: ContractSettings,
  logiscool: LogiscoolSettings,
  site: SiteSettings,
  recomandari: ReferralSettings,
  evaluare: AssessmentSettings,
  feedback: FeedbackSettings,
  emailuri: EmailSettings,
};

export default function CourseSettings({ canMutate }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = settingsTabOf(searchParams.get(SETTINGS_TAB_PARAM));

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/settings");
      setData(r.data);
    } catch (e) {
      toast.error("Nu am putut încărca setările.");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (patch, label) => {
    if (!canMutate) return;
    setBusy(label);
    try {
      const r = await api.put("/admin/settings", patch);
      setData(r.data);
      toast.success(`${label} salvat.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Eroare la salvarea ${label}.`);
    } finally {
      setBusy(null);
    }
  };

  const openTab = (key) => {
    const next = new URLSearchParams(searchParams);
    next.set(SETTINGS_TAB_PARAM, key);
    setSearchParams(next, { replace: true });
  };

  // Left and right arrows move between tabs, as a tab list is expected to.
  const onTabKey = (e, index) => {
    const step = e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : 0;
    if (!step) return;
    e.preventDefault();
    const target = SETTINGS_TABS[(index + step + SETTINGS_TABS.length) % SETTINGS_TABS.length];
    openTab(target.key);
    document.getElementById(`settings-tab-button-${target.key}`)?.focus();
  };

  if (!data) {
    return <div className="bg-white rounded-2xl border border-[#E5E7EB] p-8 flex items-center gap-2 text-[#6B7280]"><Loader2 className="animate-spin" size={18} /> Se încarcă setările…</div>;
  }

  const attention = settingsAttention(data);
  const Panel = PANELS[tab];

  return (
    <div data-testid="course-settings-panel">
      {!canMutate && (
        <div className="mb-4 rounded-xl bg-[#EDEEF0] border border-[#E5E7EB] p-3 text-sm text-[#6B7280] flex items-center gap-2" data-testid="settings-readonly-banner">
          <span className="inline-flex rounded-full bg-[#6B7280]/15 text-[#373A45] px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest">Read-only</span>
          Doar un <strong className="mx-1">owner</strong> poate modifica setările cursului. Vezi tot ce contează, fără să poți schimba.
        </div>
      )}
      {/* The line under the tabs is on the outer box and the scrolling on the
          inner one. On one box, the tabs' overlap of that line counted as
          content taller than the box, and a bar that scrolls sideways when
          needed grew an up-and-down scrollbar at its right end. */}
      <div className="mb-4 border-b border-[#E5E7EB]">
      <div className="-mb-px flex gap-1 overflow-x-auto overflow-y-hidden" role="tablist" aria-label="Secțiunile setărilor" data-testid="settings-tabs">
        {SETTINGS_TABS.map((t, index) => {
          const selected = tab === t.key;
          const notes = attention[t.key] || [];
          return (
            <button
              key={t.key}
              id={`settings-tab-button-${t.key}`}
              type="button"
              role="tab"
              aria-selected={selected}
              aria-controls="settings-tab-panel"
              tabIndex={selected ? 0 : -1}
              onClick={() => openTab(t.key)}
              onKeyDown={(e) => onTabKey(e, index)}
              className={`shrink-0 whitespace-nowrap border-b-2 px-3 py-2 text-sm font-semibold outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#965EA7]/40 ${
                selected
                  ? "border-[#965EA7] text-[#373A45]"
                  : "border-transparent text-[#6B7280] hover:text-[#373A45]"
              }`}
              data-testid={`settings-tab-${t.key}-button`}
            >
              {t.label}
              {notes.length > 0 && (
                <span className="ml-1.5 inline-block h-1.5 w-1.5 rounded-full bg-[#E63946] align-middle"
                  title={notes.join(" ")} data-testid={`settings-tab-dot-${t.key}`}>
                  <span className="sr-only"> — necesită atenție: {notes.join(" ")}</span>
                </span>
              )}
            </button>
          );
        })}
      </div>
      </div>
      <div id="settings-tab-panel" role="tabpanel" aria-labelledby={`settings-tab-button-${tab}`}>
        <Panel data={data} save={save} canMutate={canMutate} busy={busy} onReload={load} />
      </div>
    </div>
  );
}
